/**
 * @file: provider.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * App-scoped feed unread-count store. Replaces the former module-global
 * `FeedUnreadStore` singleton — a module singleton has no React lifecycle,
 * so its count leaked across account switches. The subject now lives on
 * this provider (stable via `useCreation`), mounted inside App's keyed
 * AuthContextProvider so it tears down on account switch.
 *
 * The provider polls the count while the app is foregrounded (60s);
 * `refreshFeedUnread()` reconciles after any action that flips read state.
 */

import { useServiceFactory } from '@euler/services/factory';
import { useCreation } from '@euler/utils/hooks';
import { useIsAppActive } from '@euler/utils/hooks/useIsAppActive';
import {
  FC,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { BehaviorSubject } from 'rxjs';
import { FeedUnreadContext, FeedUnreadStore } from './context';

const kPollIntervalMs = 60_000;

export const FeedUnreadProvider: FC<{ children: ReactNode }> = memo(
  ({ children }) => {
    const { feedService } = useServiceFactory();
    const active = useIsAppActive();

    const feedUnreadCount$ = useCreation(() => new BehaviorSubject(0), []);
    const inFlightRef = useRef<Promise<void> | null>(null);

    const refreshFeedUnread = useCallback(async () => {
      if (inFlightRef.current) {
        await inFlightRef.current;
        return;
      }
      inFlightRef.current = (async () => {
        try {
          const counts = await feedService.getUnreadCount();
          const total = Number(counts.__all__) || 0;
          if (feedUnreadCount$.getValue() !== total) {
            feedUnreadCount$.next(total);
          }
        } catch {
          // swallow — keep last-known value
        } finally {
          inFlightRef.current = null;
        }
      })();
      await inFlightRef.current;
    }, [feedService, feedUnreadCount$]);

    // Poll while foregrounded; paused in background, resumed on `active`.
    useEffect(() => {
      if (!active) return;
      void refreshFeedUnread();
      const handle = setInterval(
        () => void refreshFeedUnread(),
        kPollIntervalMs,
      );
      return () => clearInterval(handle);
    }, [active, refreshFeedUnread]);

    const store = useMemo<FeedUnreadStore>(
      () => ({ feedUnreadCount$, refreshFeedUnread }),
      [feedUnreadCount$, refreshFeedUnread],
    );

    return (
      <FeedUnreadContext.Provider value={store}>
        {children}
      </FeedUnreadContext.Provider>
    );
  },
);
